import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { Calendar, MessageSquare, CalendarPlus, Clock, X } from "lucide-react";

const ICON_OPTIONS = [
  { value: "calendar", label: "Calendar", Icon: Calendar },
  { value: "message", label: "Message", Icon: MessageSquare },
  { value: "calendar-plus", label: "Calendar+", Icon: CalendarPlus },
  { value: "clock", label: "Clock", Icon: Clock },
];

const POSITION_OPTIONS = [
  { value: "bottom-right", labelKey: "widget.pos_br" },
  { value: "bottom-left", labelKey: "widget.pos_bl" },
];

const WidgetSettings = () => {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const shopId = profile?.organisation_id || "YOUR_SHOP_ID";

  const [bgColor, setBgColor] = useState("#6366f1");
  const [iconColor, setIconColor] = useState("#ffffff");
  const [icon, setIcon] = useState("calendar");
  const [position, setPosition] = useState("bottom-right");
  const [autoPopup, setAutoPopup] = useState(false);
  const [autoPopupDelay, setAutoPopupDelay] = useState(5);
  const [greeting, setGreeting] = useState("");

  const embedScript = useMemo(() => {
    const origin = window.location.origin;
    const config = JSON.stringify({
      shopId,
      origin,
      bgColor,
      iconColor,
      icon,
      position,
      autoPopup,
      autoPopupDelay: autoPopup ? autoPopupDelay : 0,
      greeting: greeting || null,
    });
    return `<script>
(function(){var c=${config};var d=document;var s=d.createElement('style');s.textContent=\`
.slotify-widget-btn{position:fixed;\${c.position==='bottom-left'?'left':'right'}:20px;bottom:20px;z-index:9999;width:60px;height:60px;border-radius:50%;background:\${c.bgColor};color:\${c.iconColor};border:none;cursor:pointer;box-shadow:0 4px 16px rgba(0,0,0,.2);display:flex;align-items:center;justify-content:center;transition:transform .2s,box-shadow .2s}
.slotify-widget-btn:hover{transform:scale(1.08);box-shadow:0 6px 24px rgba(0,0,0,.3)}
.slotify-widget-btn svg{width:28px;height:28px;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
.slotify-widget-overlay{position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;opacity:0;pointer-events:none;transition:opacity .25s}
.slotify-widget-overlay.open{opacity:1;pointer-events:auto}
.slotify-widget-modal{background:#fff;border-radius:16px;width:420px;max-width:95vw;height:85vh;max-height:700px;overflow:hidden;position:relative;transform:translateY(20px);transition:transform .25s}
.slotify-widget-overlay.open .slotify-widget-modal{transform:translateY(0)}
.slotify-widget-close{position:absolute;top:12px;right:12px;z-index:1;width:32px;height:32px;border-radius:50%;border:none;background:rgba(0,0,0,.06);cursor:pointer;display:flex;align-items:center;justify-content:center;color:#333}
.slotify-widget-close:hover{background:rgba(0,0,0,.12)}
.slotify-widget-iframe{width:100%;height:100%;border:none}
.slotify-widget-greeting{position:fixed;\${c.position==='bottom-left'?'left':'right'}:20px;bottom:90px;z-index:9998;background:#fff;color:#333;padding:12px 16px;border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,.15);font:14px/1.4 -apple-system,sans-serif;max-width:240px;opacity:0;transform:translateY(8px);transition:opacity .3s,transform .3s;pointer-events:none}
.slotify-widget-greeting.show{opacity:1;transform:translateY(0);pointer-events:auto}
.slotify-widget-greeting-close{position:absolute;top:4px;right:8px;background:none;border:none;cursor:pointer;color:#999;font-size:16px}
\`;d.head.appendChild(s);
var icons={calendar:'<path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z"/>',message:'<path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>',"calendar-plus":'<path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z"/><path d="M12 14v4M10 16h4"/>',clock:'<circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>'};
var btn=d.createElement('button');btn.className='slotify-widget-btn';btn.innerHTML='<svg viewBox="0 0 24 24">'+icons[c.icon]+'</svg>';d.body.appendChild(btn);
var overlay=d.createElement('div');overlay.className='slotify-widget-overlay';overlay.innerHTML='<div class="slotify-widget-modal"><button class="slotify-widget-close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg></button><iframe class="slotify-widget-iframe" src="'+c.origin+'/shop/'+c.shopId+'?embed=true"></iframe></div>';d.body.appendChild(overlay);
function open(){overlay.classList.add('open');if(g)g.classList.remove('show')}
function close(){overlay.classList.remove('open')}
btn.onclick=open;overlay.onclick=function(e){if(e.target===overlay)close()};overlay.querySelector('.slotify-widget-close').onclick=close;
var g=null;if(c.greeting){g=d.createElement('div');g.className='slotify-widget-greeting';g.innerHTML=c.greeting+'<button class="slotify-widget-greeting-close">&times;</button>';d.body.appendChild(g);g.querySelector('.slotify-widget-greeting-close').onclick=function(){g.classList.remove('show')};setTimeout(function(){g.classList.add('show')},2000)}
if(c.autoPopup&&c.autoPopupDelay>0){setTimeout(open,c.autoPopupDelay*1000)}
})();
<\/script>`;
  }, [shopId, bgColor, iconColor, icon, position, autoPopup, autoPopupDelay, greeting]);

  const copyCode = () => {
    navigator.clipboard.writeText(embedScript);
    toast.success(t("widget.copied"));
  };

  const selectedIcon = ICON_OPTIONS.find((i) => i.value === icon) || ICON_OPTIONS[0];

  return (
    <>
      <h1 className="text-xl font-bold text-foreground mb-6">{t("widget.title")}</h1>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Configuration */}
        <div className="space-y-6">
          {/* Appearance */}
          <section className="rounded-xl border border-border bg-card p-6 shadow-card space-y-4">
            <h2 className="text-sm font-semibold text-card-foreground">{t("widget.appearance")}</h2>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("widget.bg_color")}</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="h-10 w-10 cursor-pointer rounded border border-input"
                  />
                  <Input value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="font-mono text-xs" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("widget.icon_color")}</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={iconColor}
                    onChange={(e) => setIconColor(e.target.value)}
                    className="h-10 w-10 cursor-pointer rounded border border-input"
                  />
                  <Input value={iconColor} onChange={(e) => setIconColor(e.target.value)} className="font-mono text-xs" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("widget.icon")}</Label>
              <div className="flex gap-2">
                {ICON_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setIcon(opt.value)}
                    className={`flex h-12 w-12 items-center justify-center rounded-lg border-2 transition-colors ${
                      icon === opt.value
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-background text-muted-foreground hover:border-primary/30"
                    }`}
                  >
                    <opt.Icon className="h-5 w-5" />
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("widget.position")}</Label>
              <Select value={position} onValueChange={setPosition}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {POSITION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </section>

          {/* Behavior */}
          <section className="rounded-xl border border-border bg-card p-6 shadow-card space-y-4">
            <h2 className="text-sm font-semibold text-card-foreground">{t("widget.behavior")}</h2>

            <div className="space-y-2">
              <Label>{t("widget.greeting_text")}</Label>
              <Input
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                placeholder={t("widget.greeting_placeholder")}
              />
              <p className="text-xs text-muted-foreground">{t("widget.greeting_help")}</p>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <p className="text-sm font-medium text-foreground">{t("widget.auto_popup")}</p>
                <p className="text-xs text-muted-foreground">{t("widget.auto_popup_desc")}</p>
              </div>
              <Switch checked={autoPopup} onCheckedChange={setAutoPopup} />
            </div>

            {autoPopup && (
              <div className="space-y-2">
                <Label>{t("widget.auto_popup_delay")}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={60}
                    value={autoPopupDelay}
                    onChange={(e) => setAutoPopupDelay(Number(e.target.value))}
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">{t("widget.seconds")}</span>
                </div>
              </div>
            )}
          </section>

          {/* Embed code */}
          <section className="rounded-xl border border-border bg-card p-6 shadow-card space-y-3">
            <h2 className="text-sm font-semibold text-card-foreground">{t("widget.embed_code")}</h2>
            <p className="text-xs text-muted-foreground">{t("widget.embed_desc")}</p>
            <div className="rounded-lg bg-muted p-3 max-h-40 overflow-auto">
              <code className="text-xs text-foreground break-all whitespace-pre-wrap">{embedScript}</code>
            </div>
            <Button variant="outline" onClick={copyCode}>
              {t("widget.copy_code")}
            </Button>
          </section>
        </div>

        {/* Live Preview */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-card-foreground">{t("widget.preview")}</h2>
          <div className="relative rounded-xl border border-border bg-muted/30 overflow-hidden" style={{ height: 500 }}>
            {/* Simulated webpage background */}
            <div className="absolute inset-0 p-6">
              <div className="h-4 w-32 rounded bg-muted mb-4" />
              <div className="h-3 w-full rounded bg-muted/60 mb-2" />
              <div className="h-3 w-3/4 rounded bg-muted/60 mb-2" />
              <div className="h-3 w-5/6 rounded bg-muted/60 mb-6" />
              <div className="h-24 w-full rounded-lg bg-muted/40 mb-4" />
              <div className="h-3 w-full rounded bg-muted/60 mb-2" />
              <div className="h-3 w-2/3 rounded bg-muted/60" />
            </div>

            {/* Greeting bubble */}
            {greeting && (
              <div
                className="absolute z-10 bg-card rounded-xl shadow-lg p-3 text-xs text-card-foreground max-w-[200px]"
                style={{
                  [position === "bottom-left" ? "left" : "right"]: 16,
                  bottom: 80,
                }}
              >
                {greeting}
                <button className="absolute top-1 right-2 text-muted-foreground text-xs">×</button>
              </div>
            )}

            {/* Widget button */}
            <button
              className="absolute z-10 flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-105"
              style={{
                background: bgColor,
                color: iconColor,
                [position === "bottom-left" ? "left" : "right"]: 16,
                bottom: 16,
              }}
            >
              <selectedIcon.Icon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default WidgetSettings;
